import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { ArrowLeft, AlertCircle } from 'lucide-react';
import { AuthForm } from '@/components/integrations';
import * as api from '@/lib/integrations/api';
import type { ProviderResponse } from '@/lib/integrations/types';

export const Route = createFileRoute('/_dashboard/integrations/$provider')({
  component: ProviderConnectPage,
});

function ProviderConnectPage() {
  const { provider: providerKey } = Route.useParams();
  const navigate = useNavigate();

  const [provider, setProvider] = useState<ProviderResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!providerKey) {
      setError('No provider specified');
      setLoading(false);
      return;
    }

    api
      .getProvider(providerKey)
      .then(setProvider)
      .catch((err) => setError(err.message || 'Failed to load provider'))
      .finally(() => setLoading(false));
  }, [providerKey]);

  const handleSuccess = () => {
    navigate({ to: '/integrations' });
  };

  const handleCancel = () => {
    navigate({ to: '/integrations/add' });
  };

  // Loading state
  if (loading) {
    return (
      <div className="container max-w-lg py-8">
        <Card>
          <CardHeader>
            <div className="flex items-center gap-4">
              <Skeleton className="h-10 w-10 rounded-full" />
              <div className="space-y-2">
                <Skeleton className="h-5 w-32" />
                <Skeleton className="h-4 w-24" />
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Error state
  if (error || !provider) {
    return (
      <div className="container max-w-lg py-8">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-destructive">
              <AlertCircle className="size-5" />
              Error Loading Provider
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-muted-foreground">
              {error || 'Provider not found'}
            </p>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => navigate({ to: '/integrations/add' })}>
                <ArrowLeft className="mr-2 size-4" />
                Go Back
              </Button>
              <Button onClick={() => navigate({ to: '/integrations' })}>
                View All Integrations
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Success - render auth form
  return (
    <div className="container max-w-lg py-8">
      <Button
        variant="ghost"
        size="sm"
        className="mb-4"
        onClick={() => navigate({ to: '/integrations/add' })}
      >
        <ArrowLeft className="mr-2 size-4" />
        Back
      </Button>

      <Card>
        <CardHeader>
          <CardTitle>Connect {provider.display_name || provider.provider}</CardTitle>
        </CardHeader>
        <CardContent>
          <AuthForm
            provider={provider}
            onSuccess={handleSuccess}
            onCancel={handleCancel}
          />
        </CardContent>
      </Card>
    </div>
  );
}
